import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { isValidGln } from 'support/helpers/parties/gln';
import { IdentifierDomainOptions, PartnerDTO } from 'support/types';
import { Input } from 'components/Form/Input/Input/Input';
import { Radio } from 'components/Form/Radio/Radio';

export type PartnerIdentifierType = {
  partyIdentifierDomain?: IdentifierDomainOptions | undefined;
  partyIdentifier?: string | undefined;
};

const PREFERRED_DOMAIN_TO_SUBMISSION_DOMAIN: Record<string, IdentifierDomainOptions> = {
  internal: IdentifierDomainOptions.RECEIVER_INTERNAL,
  GS1: IdentifierDomainOptions.GS1,
};

const NETWORK_SKIP_DOMAINS = new Set<IdentifierDomainOptions>([IdentifierDomainOptions.GS1]);

export const getAvailableDomains = (
  preferredIdentifierDomain: PartnerDTO['preferredIdentifierDomain'],
  partnerInNetwork: boolean,
): Array<IdentifierDomainOptions> => {
  const allDomains = preferredIdentifierDomain?.length
    ? preferredIdentifierDomain
        .map((d) => PREFERRED_DOMAIN_TO_SUBMISSION_DOMAIN[d])
        .filter((d): d is IdentifierDomainOptions => d !== undefined)
    : [IdentifierDomainOptions.GS1];
  if (!partnerInNetwork) {
    return allDomains;
  }
  return allDomains.filter((d) => !NETWORK_SKIP_DOMAINS.has(d));
};

type PartnerIdentifierSchemaOptions = {
  connected: boolean;
  preferredIdentifierDomain: PartnerDTO['preferredIdentifierDomain'];
  partnerInNetwork?: boolean;
  ownGlns?: Set<string>;
};

type PartnerIdentifierSchemaReturn = Yup.ObjectSchema<PartnerIdentifierType | Required<PartnerIdentifierType>>;

export const PartnerIdentifierSchema = (
  t: TFunction,
  options: PartnerIdentifierSchemaOptions,
): PartnerIdentifierSchemaReturn => {
  const { connected, preferredIdentifierDomain, partnerInNetwork = false, ownGlns = new Set<string>() } = options;

  const availableDomains = getAvailableDomains(preferredIdentifierDomain, partnerInNetwork);
  const isRequired = !connected && availableDomains.length > 0;

  if (!isRequired) {
    return Yup.object({
      partyIdentifierDomain: Yup.mixed<IdentifierDomainOptions>().oneOf(Object.values(IdentifierDomainOptions)),
      partyIdentifier: Yup.string(),
    });
  }

  const hasMultipleDomains = availableDomains.length > 1;

  // t('partners:request.partyIdentifier.required.glnError')
  // t('partners:request.partyIdentifier.required.error')
  // t('partners:request.partyIdentifierDomain.required.error')
  // t('partners:request.partyIdentifier.invalidGlnError')
  // t('partners:request.partyIdentifier.ownGlnError')
  return Yup.object().shape({
    partyIdentifierDomain: hasMultipleDomains
      ? Yup.mixed<IdentifierDomainOptions>()
          .oneOf(Object.values(IdentifierDomainOptions))
          .required(t('partners:request.partyIdentifierDomain.required.error'))
      : Yup.mixed<IdentifierDomainOptions>().oneOf(Object.values(IdentifierDomainOptions)),
    partyIdentifier: Yup.string()
      .when('partyIdentifierDomain', {
        is: IdentifierDomainOptions.GS1,
        then: (schema) => schema.required(t('partners:request.partyIdentifier.required.glnError')),
        otherwise: (schema) => schema.required(t('partners:request.partyIdentifier.required.error')),
      })
      // Both checks are GS1-only: an internal identifier is free-form. Values are trimmed
      // first and submitted trimmed, matching the backend rule which rejects whitespace.
      .test('validGln', t('partners:request.partyIdentifier.invalidGlnError'), function (value) {
        if (this.parent.partyIdentifierDomain !== IdentifierDomainOptions.GS1 || !value) {
          return true;
        }
        return isValidGln(value.trim());
      })
      .test('notOwnGln', t('partners:request.partyIdentifier.ownGlnError'), function (value) {
        if (this.parent.partyIdentifierDomain !== IdentifierDomainOptions.GS1 || !value) {
          return true;
        }
        return !ownGlns.has(value.trim());
      }),
  });
};

type PartnerIdentifiersProps = {
  preferredIdentifierDomain: PartnerDTO['preferredIdentifierDomain'];
  partnerInNetwork?: boolean;
};

// t('partners:request.identifier.GS1.input.label')
// t('partners:request.identifier.GS1.input.description')
// t('partners:request.identifier.GS1.radio.label')
// t('partners:request.identifier.GS1.radio.description')
// t('partners:request.identifier.RECEIVER_INTERNAL.input.label')
// t('partners:request.identifier.RECEIVER_INTERNAL.input.description')
// t('partners:request.identifier.RECEIVER_INTERNAL.radio.label')
// t('partners:request.identifier.RECEIVER_INTERNAL.radio.description')
// t('partners:request.identifier.domainSelection.label')
export const PartnerIdentifiers = ({
  preferredIdentifierDomain,
  partnerInNetwork = false,
}: PartnerIdentifiersProps) => {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<PartnerIdentifierType>();

  const availableDomains = useMemo(
    () => getAvailableDomains(preferredIdentifierDomain, partnerInNetwork),
    [preferredIdentifierDomain, partnerInNetwork],
  );

  const hasMultipleDomains = availableDomains.length > 1;
  const selectedDomain = useWatch<PartnerIdentifierType, 'partyIdentifierDomain'>({ name: 'partyIdentifierDomain' });

  useEffect(() => {
    if (availableDomains.length === 1) {
      setValue('partyIdentifierDomain', availableDomains[0], { shouldValidate: true });
    } else if (availableDomains.length > 1) {
      setValue('partyIdentifierDomain', undefined);
    }
    setValue('partyIdentifier', '', { shouldValidate: false });
  }, [availableDomains, setValue]);

  if (availableDomains.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="text-base leading-6 font-semibold text-gray-900">{t('partners:request.identifier.title')}</div>
        <div className="text-sm leading-5 text-gray-700">{t('partners:request.identifier.description')}</div>
      </div>
      {hasMultipleDomains && (
        <Radio.Group label={t('partners:request.identifier.domainSelection.label')} required className="mt-4">
          {availableDomains.map((domain) => (
            <Radio.Item
              key={domain}
              label={t(`partners:request.identifier.${domain}.radio.label`)}
              description={t(`partners:request.identifier.${domain}.radio.description`)}
              value={domain}
              hasErrors={!!errors.partyIdentifierDomain}
              {...register('partyIdentifierDomain', {
                onChange: () => setValue('partyIdentifier', ''),
              })}
            />
          ))}
          {errors.partyIdentifierDomain?.message && (
            <p className="text-sm text-red-600">{errors.partyIdentifierDomain.message}</p>
          )}
        </Radio.Group>
      )}
      {selectedDomain && (
        <Input
          label={t(`partners:request.identifier.${selectedDomain}.input.label`)}
          {...register('partyIdentifier')}
          errors={errors.partyIdentifier}
          description={t(`partners:request.identifier.${selectedDomain}.input.description`)}
          required
        />
      )}
    </div>
  );
};
